import { useMemo } from 'react';
// routes
import { PATH_AUTH, PATH_DASHBOARD } from '../../../routes/paths';

// locales
import { useLocales } from 'src/locales';
// components
import Iconify from 'src/components/iconify';
import SvgColor from 'src/components/svg-color';
import { HandleLogout } from 'src/pages/standard-components/Logout';
import { routePrefix } from 'src/utils/methods/StandardMethods';
import { useAuthContext } from 'src/auth/hooks';
import { ROLES } from 'src/utils/constants/constants';
import { AuthUserType } from 'src/auth/types';


// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  user: icon('ic_user'),
  home: <Iconify icon='ant-design:home-twotone' />,
  book: <Iconify icon='ant-design:book-twotone' />,
  dashboard: icon('ic_dashboard'),
  push_notiifcation: icon('ic_chat'),
  account_setting: <Iconify icon='material-symbols:account-balance-rounded' />,
  logout: <Iconify icon='material-symbols:logout' />,
  email_notification: <Iconify icon='fxemoji:email' />,
  transaction: <Iconify icon='ant-design:dollar-twotone' />,
  modelTrnsx: <Iconify icon='icon-park-outline:transaction' />,
  trnsxSync: <Iconify icon='material-symbols:sync' />,
  compression: <Iconify icon="material-symbols:compress" />,
  beat: <Iconify icon='solar:calendar-bold-duotone' />,
  invoice: <Iconify icon='iconamoon:invoice-duotone' />,
  organization: <Iconify icon='clarity:organization-solid' />,
  model: <Iconify icon='carbon:model-alt' />,
  plan: <Iconify icon='icon-park-outline:list' />,
  audit_logs: <Iconify icon='icon-park:upload-logs' />,
  theme_setting: <Iconify icon='material-symbols:settings-night-sight-outline' />,
  file_upload: <Iconify icon='uim:upload-alt' />,
  DOCUMENT_MANAGER: <Iconify icon='entypo:folder' />,
  DOCUMENT_MANAGER_SHARED: <Iconify icon='fluent:people-20-filled' />,
  DOCUMENT_MANAGER_BIN: <Iconify icon='solar:trash-bin-2-bold-duotone' />,
  subscription_management: <Iconify icon='eos-icons:subscription-management' />,
  subscription: <Iconify icon='wpf:renew-subscription' />,
  data_processing: <Iconify icon='fluent-mdl2:machine-learning' />,
  folder_upload: <Iconify icon='material-symbols:drive-folder-upload' />,
  upload: <Iconify icon='uim:upload-alt' />,
  templates: <Iconify icon='tabler:template' />,
};

// const { user } = useAuthContext();

export function ConfigNavigation() {
  const { user } = useAuthContext();
  switch (user?.role) {
    case ROLES.SUPER_ADMIN:
      return NavSuperAdminConfig(user);
    case ROLES.SITE_ADMIN:
      return NavSiteAdminConfig(user);
    case ROLES.ORG_ADMIN:
      return NavOrgAdminConfig(user);
    case ROLES.ORG_USER:
      return NavIndividualUserConfig(user);
    case ROLES.INDIVIDUAL_USER:
      return NavSiteAdminConfig(user);
    default:
      return NavSuperAdminConfig(user);
  }
}


///////////////////////
// ----------------------------------------------------------------------

export function NavSuperAdminConfig(user: AuthUserType) {
  const { translate } = useLocales();
  const data = useMemo(
    () => [
      // GENERAL
      // ----------------------------------------------------------------------
      {
        subheader: translate('Dashboard'),
        items: [
          { title: translate('Dashboard'), path: PATH_DASHBOARD.dashboard.dash(routePrefix(user)), icon: ICONS.dashboard },
          { title: translate('Transactions'), path: PATH_DASHBOARD?.transaction?.list(routePrefix(user)), icon: ICONS.transaction },
          { title: translate('Model Transactions'), path: PATH_DASHBOARD?.modelTransaction?.list(routePrefix(user)), icon: ICONS.modelTrnsx },
          { title: translate('Transactions Sync'), path: PATH_DASHBOARD?.transactionSync?.list(routePrefix(user)), icon: ICONS.trnsxSync },
          { title: translate('Compression'), path: PATH_DASHBOARD.compression.list(routePrefix(user)), icon: ICONS.compression },
        ],
      },
      {
        subheader: 'User Upload',
        items: [
          { title: translate('File Upload'), path: PATH_DASHBOARD.fileUploads.admin(routePrefix(user)), icon: ICONS.file_upload }
        ]
      },
      // MANAGEMENT
      // ----------------------------------------------------------------------
      {
        subheader: translate('Management'),
        items: [
          { title: translate('User'), path: PATH_DASHBOARD.user.list(routePrefix(user)), icon: ICONS.user },
          { title: translate('Organization'), path: PATH_DASHBOARD.organization.list(routePrefix(user)), icon: ICONS.organization },
        ],
      },
      // Plans
      // ----------------------------------------------------------------------
      {
        subheader: translate('Plans'),
        items: [
          { title: translate('Model'), path: PATH_DASHBOARD.model.list(routePrefix(user)), icon: ICONS.model },
          { title: translate('Plans'), path: PATH_DASHBOARD.plans.list(routePrefix(user)), icon: ICONS.plan },
        ],
      },
      // Theme
      // ----------------------------------------------------------------------
      {
        subheader: translate('Audit Trials'),
        items: [
          { title: translate('Logs'), path: PATH_DASHBOARD.auditLog.list(routePrefix(user)), icon: ICONS.audit_logs },
        ]
      },
      // Account Settings
      // ----------------------------------------------------------------------
      // {
      //   subheader: translate('Profile'),
      //   items: [
      //     { title: translate('Profile'), path: PATH_DASHBOARD.accountSettings.settings(routePrefix(user)), icon: ICONS.account_setting },
      //     { title: translate('Logout'), path: PATH_AUTH.logout, icon: ICONS.logout, function: HandleLogout },
      //   ],
      // },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate]
  );

  return data;

}

export function NavSiteAdminConfig(user: AuthUserType) {
  const { translate } = useLocales();
  const data = useMemo(
    () => [
      // GENERAL
      // ----------------------------------------------------------------------
      {
        subheader: translate('Dashboard'),
        items: [
          { title: translate('Dashboard'), path: PATH_DASHBOARD.dashboard.dash(routePrefix(user)), icon: ICONS.dashboard },
          { title: translate('Transactions'), path: PATH_DASHBOARD?.transaction?.list(routePrefix(user)), icon: ICONS.transaction },
          // { title: translate('Custom Templates'), path: PATH_DASHBOARD.customTemplates.create(routePrefix(user)), icon: ICONS.templates },
        ],
      },
      // MANAGEMENT
      // ----------------------------------------------------------------------
      {
        subheader: translate('Management'),
        items: [
          { title: translate('User'), path: PATH_DASHBOARD.user.list(routePrefix(user)), icon: ICONS.user },
          { title: translate('Organization'), path: PATH_DASHBOARD.organization.list(routePrefix(user)), icon: ICONS.organization },
        ],
      },
      // Account Settings
      // ----------------------------------------------------------------------
      {
        subheader: translate('Profile'),
        items: [
          { title: translate('Profile'), path: PATH_DASHBOARD.accountSettings.settings(routePrefix(user)), icon: ICONS.account_setting },
          { title: translate('Logout'), path: PATH_AUTH.logout, icon: ICONS.logout, function: HandleLogout },
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate]
  );

  return data;
}

export function NavOrgAdminConfig(user: AuthUserType) {
  const { translate } = useLocales();

  const data = useMemo(
    () => [
      // GENERAL
      // ----------------------------------------------------------------------
      {
        subheader: translate(''),
        items: [
          { title: translate('Dashboard'), path: PATH_DASHBOARD.dashboard.dash(routePrefix(user)), icon: ICONS.dashboard },
          // { title: translate('File Upload'), path: PATH_DASHBOARD.fileUploads.main(routePrefix(user)), icon: ICONS.file_upload },
          { title: translate('Invoices'), path: PATH_DASHBOARD.invoice.list(routePrefix(user)), icon: ICONS.invoice },
          { title: translate('Transactions'), path: PATH_DASHBOARD?.orgTransaction?.list(routePrefix(user)), icon: ICONS.transaction },
          // { title: translate('Upload'), path: PATH_DASHBOARD.upload.upload(routePrefix(user)), icon: ICONS.upload },
          // { title: translate('Custom Templates'), path: PATH_DASHBOARD.customTemplates.create(routePrefix(user)), icon: ICONS.templates },
        ],
      },
      // MANAGEMENT
      // ----------------------------------------------------------------------
      // {
      //   subheader: translate('Management'),
      //   items: [
      //     { title: translate('User'), path: PATH_DASHBOARD.user.list(routePrefix(user)), icon: ICONS.user },
      //   ],
      // },
      // {
      //   subheader: translate('File Manager'),
      //   items: [
      //     { title: translate('My Files'), path: PATH_DASHBOARD.fileManager.main(routePrefix(user)), icon: ICONS.DOCUMENT_MANAGER },
      //     { title: translate('Shared with me'), path: PATH_DASHBOARD.fileManager.sharedWith(routePrefix(user)), icon: ICONS.DOCUMENT_MANAGER_SHARED },
      //     { title: translate('Recycle Bin'), path: PATH_DASHBOARD.fileManager.bin(routePrefix(user)), icon: ICONS.DOCUMENT_MANAGER_BIN },
      //   ],
      // },
      {
        subheader: translate('Subscriptions'),
        items: [
          { title: translate('Plans'), path: PATH_DASHBOARD.plans.list(routePrefix(user)), icon: ICONS.plan },
          // { title: translate('Organization Subscriptions'), path: PATH_DASHBOARD.subscription.orgList, icon: ICONS.subscription_management },
          // { title: translate('My Subscriptions'), path: PATH_DASHBOARD.subscription.myList(routePrefix(user)), icon: ICONS.subscription },
        ],
      },
      //DATA TRAINING
      // {
      //   subheader: translate('Data Training'),
      //   items: [
      //     // { title: translate('Folder Upload'), path: PATH_DASHBOARD.folderUploads.main(routePrefix(user)), icon: ICONS.folder_upload },
      //     { title: translate('Data Training'), path: PATH_DASHBOARD.dataTraining.list(routePrefix(user)), icon: ICONS.data_processing },
      //   ],
      // },
      // Account Settings
      // ----------------------------------------------------------------------
      {
        subheader: translate('Profile'),
        items: [
          { title: translate('Profile'), path: PATH_DASHBOARD.user.detail2('profile', routePrefix(user)), icon: ICONS.account_setting },
          // { title: translate('Logout'), path: PATH_AUTH.logout, icon: ICONS.logout, function: HandleLogout },
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate]
  );

  return data;
}

export function NavOrgUserConfig(user: AuthUserType) {
  const { translate } = useLocales();
  const data = useMemo(
    () => [
      // GENERAL
      // ----------------------------------------------------------------------
      {
        subheader: translate(''),
        items: [
          { title: translate('Dashboard'), path: PATH_DASHBOARD.dashboard.dash(routePrefix(user)), icon: ICONS.dashboard },
          { title: translate('File Upload'), path: PATH_DASHBOARD.fileUploads.main(routePrefix(user)), icon: ICONS.file_upload },
          { title: translate('Transactions'), path: PATH_DASHBOARD?.transaction?.list(routePrefix(user)), icon: ICONS.transaction },
          // { title: translate('Custom Templates'), path: PATH_DASHBOARD.customTemplates.create(routePrefix(user)), icon: ICONS.templates },
        ],
      },
      // {
      //   subheader: translate('File Manager'),
      //   items: [
      //     { title: translate('My Files'), path: PATH_DASHBOARD.fileManager.main(routePrefix(user)), icon: ICONS.DOCUMENT_MANAGER },
      //     { title: translate('Shared with me'), path: PATH_DASHBOARD.fileManager.sharedWith(routePrefix(user)), icon: ICONS.DOCUMENT_MANAGER_SHARED },
      //     { title: translate('Recycle Bin'), path: PATH_DASHBOARD.fileManager.bin(routePrefix(user)), icon: ICONS.DOCUMENT_MANAGER_BIN },
      //   ],
      // },
      {
        subheader: translate('Subscriptions'),
        items: [
          { title: translate('My Subscriptions'), path: PATH_DASHBOARD.subscription.myList(routePrefix(user)), icon: ICONS.subscription },
        ],
      },
      //DATA TRAINING
      // {
      //   subheader: translate('Data Training'),
      //   items: [
      //     { title: translate('Folder Upload'), path: PATH_DASHBOARD.folderUploads.main(routePrefix(user)), icon: ICONS.folder_upload },
      //     { title: translate('Data Training'), path: PATH_DASHBOARD.dataTraining.list(routePrefix(user)), icon: ICONS.data_processing },
      //   ],
      // },

      // Account Settings
      // ----------------------------------------------------------------------
      {
        subheader: translate('Profile'),
        items: [
          { title: translate('Profile'), path: PATH_DASHBOARD.accountSettings.settings(routePrefix(user)), icon: ICONS.account_setting },
          { title: translate('Logout'), path: PATH_AUTH.logout, icon: ICONS.logout, function: HandleLogout },
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate]
  );

  return data;

}

export function NavIndividualUserConfig(user: AuthUserType) {
  const { translate } = useLocales();

  const data = useMemo(
    () => [
      // GENERAL
      // ----------------------------------------------------------------------
      {
        subheader: translate(''),
        items: [
          { title: translate('Dashboard'), path: PATH_DASHBOARD.dashboard.dash(routePrefix(user)), icon: ICONS.dashboard },
          { title: translate('File Upload'), path: PATH_DASHBOARD.fileUploads.main(routePrefix(user)), icon: ICONS.file_upload },
          { title: translate('Invoices'), path: PATH_DASHBOARD.invoice.list(routePrefix(user)), icon: ICONS.invoice },
          { title: translate('Transactions'), path: PATH_DASHBOARD?.transaction?.list(routePrefix(user)), icon: ICONS.transaction },
          // { title: translate('Custom Templates'), path: PATH_DASHBOARD.customTemplates.create(routePrefix(user)), icon: ICONS.templates },
        ],
      },
      // {
      //   subheader: translate('File Manager'),
      //   items: [
      //     { title: translate('My Files'), path: PATH_DASHBOARD.fileManager.main(routePrefix(user)), icon: ICONS.DOCUMENT_MANAGER },
      //     { title: translate('Shared with me'), path: PATH_DASHBOARD.fileManager.sharedWith(routePrefix(user)), icon: ICONS.DOCUMENT_MANAGER_SHARED },
      //     { title: translate('Recycle Bin'), path: PATH_DASHBOARD.fileManager.bin(routePrefix(user)), icon: ICONS.DOCUMENT_MANAGER_BIN },
      //   ],
      // },
      {
        subheader: translate('Subscriptions'),
        items: [
          { title: translate('Plans'), path: PATH_DASHBOARD.plans.list(routePrefix(user)), icon: ICONS.plan },
          { title: translate('My Subscriptions'), path: PATH_DASHBOARD.subscription.myList(routePrefix(user)), icon: ICONS.subscription },
        ],
      },
      //DATA TRAINING
      // {
      //   subheader: translate('Data Training'),
      //   items: [
      //     { title: translate('Folder Upload'), path: PATH_DASHBOARD.folderUploads.main(routePrefix(user)), icon: ICONS.folder_upload },
      //     { title: translate('Data Training'), path: PATH_DASHBOARD.dataTraining.list(routePrefix(user)), icon: ICONS.data_processing },
      //   ],
      // },
      // Account Settings
      // ----------------------------------------------------------------------
      {
        subheader: translate('Profile'),
        items: [
          { title: translate('Profile'), path: PATH_DASHBOARD.accountSettings.settings(routePrefix(user)), icon: ICONS.account_setting },
          { title: translate('Logout'), path: PATH_AUTH.logout, icon: ICONS.logout, function: HandleLogout },
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate]
  );

  return data;
}